/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Pieces shared by the v2 Real Estate screens (design-reference
 * "Real Estate · Home / Listings / Listing record"): panels, stat tiles,
 * pills, and the listing words and prices in the tenant's country.
 */

import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { v2 as t, v2EditionAccents, v2Tones } from "styles/tokens/v2";
import { defaultCurrency, isAustralia, reLocale } from "../variant";

export const RE_ACCENT = v2EditionAccents.realestate;

/** True when the active theme is v2 (the per-tenant ui_version flag). */
export const useIsReV2 = () => useTheme()?.pearlUi === "v2";

/** Classic or v2 page by the active theme (routes.js mounts both). */
export function ByUi({ legacy, v2 }) {
  return useTheme()?.pearlUi === "v2" ? v2 : legacy;
}
ByUi.propTypes = { legacy: PropTypes.node.isRequired, v2: PropTypes.node.isRequired };

export function Panel({ title, aside, children, sx }) {
  return (
    <Box
      component="section"
      sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: "10px", p: 2.5, ...sx }}
    >
      {(title || aside) && (
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, mb: 2 }}>
          {title && <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600, color: t.text }}>{title}</Typography>}
          {aside && <Typography sx={{ fontSize: 13, color: t.text3 }}>{aside}</Typography>}
        </Box>
      )}
      {children}
    </Box>
  );
}
Panel.propTypes = { title: PropTypes.node, aside: PropTypes.node, children: PropTypes.node, sx: PropTypes.object };
Panel.defaultProps = { title: null, aside: null, children: null, sx: {} };

export function Pill({ children, tone }) {
  const c = v2Tones[tone] || v2Tones.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex", alignItems: "center", height: 22, px: 1, borderRadius: "11px",
        fontSize: 12, fontWeight: 600, bgcolor: c.bg, color: c.fg, whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}
Pill.propTypes = { children: PropTypes.node.isRequired, tone: PropTypes.string };
Pill.defaultProps = { tone: "neutral" };

export function StatTile({ label, value, hint, hintTone, onClick }) {
  return (
    <Panel sx={{ p: 2.25, cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { borderColor: t.border2 } : {} }}>
      <Box onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      >
        <Typography sx={{ fontSize: 14, color: t.text2 }}>{label}</Typography>
        <Typography sx={{ fontSize: 28, fontWeight: 600, color: t.text, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>
          {value}
        </Typography>
        {hint && <Box sx={{ mt: 1 }}><Pill tone={hintTone}>{hint}</Pill></Box>}
      </Box>
    </Panel>
  );
}
StatTile.propTypes = {
  label: PropTypes.node.isRequired, value: PropTypes.node, hint: PropTypes.node, hintTone: PropTypes.string, onClick: PropTypes.func,
};
StatTile.defaultProps = { value: "—", hint: null, hintTone: "neutral", onClick: null };

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

/** $1.15M / AED 1.15M: short money for cards and tiles. */
export function shortMoney(value, currency) {
  const n = num(value);
  if (n === null || Number.isNaN(n)) return null;
  try {
    return new Intl.NumberFormat(reLocale(), {
      style: "currency", currency: currency || defaultCurrency(), notation: "compact", maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency || defaultCurrency()} ${n}`;
  }
}

/** Full money without decimals: $890,000. */
export function wholeMoney(value, currency) {
  const n = num(value);
  if (n === null || Number.isNaN(n)) return null;
  try {
    return new Intl.NumberFormat(reLocale(), {
      style: "currency", currency: currency || defaultCurrency(), maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || defaultCurrency()} ${n}`;
  }
}

const RENT_PERIOD = { WEEKLY: "per week", FORTNIGHTLY: "per fortnight", MONTHLY: "per month", QUARTERLY: "per quarter", SEMI_ANNUAL: "per half-year", ANNUAL: "per year" };

/** The price line a buyer or renter reads. Pure. */
export function listingPrice(listing) {
  if (!listing || num(listing.price) === null) return "Contact agent";
  if (listing.purpose === "RENT") {
    const period = RENT_PERIOD[listing.rent_frequency] || (isAustralia() ? "per week" : "per year");
    return `${wholeMoney(listing.price, listing.currency)} ${period}`;
  }
  return num(listing.price) >= 1e6 ? shortMoney(listing.price, listing.currency) : wholeMoney(listing.price, listing.currency);
}

const shortDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(reLocale(), { day: "numeric", month: "short" });
};

/** Card / record badge for a listing: words and tone. Pure. */
export function listingBadge(listing) {
  if (!listing) return { label: "", tone: "neutral" };
  const s = listing.status;
  if (s === "UNDER_OFFER") return { label: "Under offer", tone: "success" };
  if (s === "CLOSED") return { label: listing.purpose === "RENT" ? "Leased" : "Sold", tone: "neutral" };
  if (s === "WITHDRAWN") return { label: "Withdrawn", tone: "danger" };
  if (s === "EXPIRED") return { label: "Expired", tone: "danger" };
  if (s === "DRAFT") return { label: "Off market", tone: "neutral" };
  if (listing.sale_method === "AUCTION" && listing.auction_at) return { label: `Auction ${shortDate(listing.auction_at)}`, tone: "warning" };
  if (listing.purpose === "RENT") return { label: isAustralia() ? "For lease" : "For rent", tone: "purple" };
  return { label: "For sale", tone: "info" };
}

export const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

export function Avatar({ name }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 26, height: 26, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 11, fontWeight: 700, bgcolor: RE_ACCENT.ink, color: "#FFFFFF", flexShrink: 0,
      }}
    >
      {initialsOf(name)}
    </Box>
  );
}
Avatar.propTypes = { name: PropTypes.string };
Avatar.defaultProps = { name: "" };

/** First photo URL a listing carries in `media`, if any. */
export function coverUrl(listing) {
  const first = (listing?.media || [])[0];
  if (!first) return null;
  if (typeof first === "string") return first;
  return first.url || first.thumbnail_url || first.src || null;
}
